import React, {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import {
  cleanupExpiredNotifications,
  deleteAllNotifications as deleteAllNotificationsService,
  deleteNotification as deleteNotificationService,
  markAllNotificationsAsRead,
  markNotificationAsRead,
  subscribeToNotifications,
  subscribeToUnreadCount,
} from '../services/notificationService';
import type { Notification } from '../types';
import { useAuth } from './AuthContext';

interface NotificationContextType {
  notifications: Notification[];
  unreadCount: number;
  loading: boolean;
  markAsRead: (notificationId: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  refreshNotifications: () => void;
  deleteNotification: (notificationId: string) => Promise<void>;
  deleteAllNotifications: () => Promise<void>;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

interface NotificationProviderProps {
  children: ReactNode;
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({ children }) => {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);

  // 通知をリアルタイム監視
  useEffect(() => {
    if (!user?.uid) {
      setNotifications([]);
      setUnreadCount(0);
      setLoading(false);
      return;
    }

    setLoading(true);

    let unsubscribeNotifications: (() => void) | null = null;
    let unsubscribeUnreadCount: (() => void) | null = null;

    try {
      // 通知一覧の監視
      unsubscribeNotifications = subscribeToNotifications(user.uid, (newNotifications) => {
        setNotifications(newNotifications);
        setLoading(false);
      });
    } catch (error) {
      console.error('Failed to subscribe to notifications:', error);
      setLoading(false);
    }

    try {
      // 未読数の監視
      unsubscribeUnreadCount = subscribeToUnreadCount(user.uid, (count) => {
        setUnreadCount(count);
      });
    } catch (error) {
      console.error('Failed to subscribe to unread count:', error);
    }

    return () => {
      if (unsubscribeNotifications) unsubscribeNotifications();
      if (unsubscribeUnreadCount) unsubscribeUnreadCount();
    };
  }, [user?.uid]);

  // 初回ロード時にTTLクリーンアップを実行
  useEffect(() => {
    if (!user?.uid) return;

    // 期限切れ通知を削除
    cleanupExpiredNotifications(user.uid)
      .then((count) => {
        if (count > 0) {
          console.log(`Cleaned up ${count} expired notifications`);
        }
      })
      .catch((error) => {
        console.error('Failed to cleanup expired notifications:', error);
      });
  }, [user?.uid]);

  // 単一の通知を既読にする
  const markAsRead = useCallback(async (notificationId: string) => {
    try {
      await markNotificationAsRead(notificationId);
      // ローカル状態も更新（リアルタイム監視で更新されるが、即時反映のため）
      setNotifications((prev) =>
        prev.map((n) => (n.id === notificationId ? { ...n, read: true } : n)),
      );
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  }, []);

  // 全ての通知を既読にする
  const markAllAsRead = useCallback(async () => {
    if (!user?.uid) return;

    try {
      await markAllNotificationsAsRead(user.uid);
      // ローカル状態も更新
      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Failed to mark all notifications as read:', error);
    }
  }, [user?.uid]);

  // 手動でリフレッシュ（通常はリアルタイム監視で自動更新）
  const refreshNotifications = useCallback(() => {
    // onSnapshotで自動更新されるため、特に処理不要
    // 必要に応じてここで強制再取得を実装可能
  }, []);

  // 単一の通知を削除する
  const deleteNotification = useCallback(async (notificationId: string) => {
    try {
      await deleteNotificationService(notificationId);
      // ローカル状態も更新（リアルタイム監視で更新されるが、即時反映のため）
      setNotifications((prev) => prev.filter((n) => n.id !== notificationId));
    } catch (error) {
      console.error('Failed to delete notification:', error);
    }
  }, []);

  // 全ての通知を削除する
  const deleteAllNotifications = useCallback(async () => {
    if (!user?.uid) return;

    try {
      await deleteAllNotificationsService(user.uid);
      // ローカル状態も更新
      setNotifications([]);
      setUnreadCount(0);
    } catch (error) {
      console.error('Failed to delete all notifications:', error);
    }
  }, [user?.uid]);

  const value: NotificationContextType = {
    notifications,
    unreadCount,
    loading,
    markAsRead,
    markAllAsRead,
    refreshNotifications,
    deleteNotification,
    deleteAllNotifications,
  };

  return <NotificationContext.Provider value={value}>{children}</NotificationContext.Provider>;
};

export const useNotifications = (): NotificationContextType => {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

export default NotificationContext;
